import type { AggregateRating, Person, Rating } from 'schema-dts';

const BEST_RATING = 5;
const WORST_RATING = 1;
const AUTHOR_NAME_MAX_LENGTH = 100;

const BLOCK_HTML_TAG = /<\/?(p|br|div|li|ul|ol|h[1-6]|blockquote|tr|td)\b[^>]*>/gi;
const HTML_TAG = /<[^>]*>/g;
const HTML_ENTITY = /&(amp|lt|gt|quot|#39|nbsp);/g;
const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

const toStars = (rating: number | null | undefined) => {
  if (rating == null) return null;
  const stars = Math.round(rating * BEST_RATING * 100) / 100;
  return stars >= WORST_RATING ? stars : null;
};

export const buildRating = (rating: number | null | undefined): Rating | undefined => {
  const stars = toStars(rating);
  if (stars == null) return undefined;

  return { '@type': 'Rating', ratingValue: stars, bestRating: BEST_RATING, worstRating: WORST_RATING };
};

export const buildAggregateRating = (
  averageRating: number | null | undefined,
  reviewCount: number | null | undefined,
): AggregateRating | undefined => {
  const stars = toStars(averageRating);
  if (stars == null || reviewCount == null || reviewCount < 1) return undefined;

  return {
    '@type': 'AggregateRating',
    ratingValue: stars,
    reviewCount,
    bestRating: BEST_RATING,
    worstRating: WORST_RATING,
  };
};

export const buildReviewAuthor = (name: string | null | undefined): Person | undefined => {
  const trimmedName = name?.trim();
  if (trimmedName == null || trimmedName === '' || trimmedName.length > AUTHOR_NAME_MAX_LENGTH) return undefined;

  return { '@type': 'Person', name: trimmedName };
};

export const toPlainText = (html: string | null | undefined) => {
  const text = html
    ?.replace(BLOCK_HTML_TAG, ' ')
    .replace(HTML_TAG, '')
    .replace(HTML_ENTITY, (entity) => HTML_ENTITIES[entity])
    .replace(/\s+/g, ' ')
    .trim();

  return text != null && text !== '' ? text : undefined;
};
